import {
    CSSProperties,
    useCallback, useEffect, useRef, useState,
} from 'react'

type Props = {
    dropdownItemHeight: number
    dropdownItemCount: number
}

const useSmartDropdownPositioning = ({
    dropdownItemHeight,
    dropdownItemCount,
}: Readonly<Props>) => {
    const [
        isVisible,
        setIsVisible,
    ] = useState(false)
    const [
        positionStyles,
        setPositionStyles,
    ] = useState<CSSProperties>({
        top: 0,
    })
    const dropdownRef = useRef(null)
    const containerRef = useRef(null)

    const getPositionStyles = useCallback(() => {
        if (!containerRef.current) {
            return {
                top: 0,
            }
        }

        const containerRect = containerRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        const estimatedDropdownHeight = dropdownItemCount * dropdownItemHeight

        const wouldOverflow = containerRect.bottom + estimatedDropdownHeight > viewportHeight

        const downwardPosition = {
            top: 0,
            bottom: 'auto',
        }

        if (wouldOverflow) {
            if (dropdownItemCount < 5) {
                // Position dropdown to span upward
                return {
                    bottom: 0,
                    top: 'auto',
                }
            }
            // Position dropdown to span downward with scroll
            return {
                ...downwardPosition,
                height: viewportHeight - containerRect.top - 10, // 10px margin below dropdown
                overflowY: 'auto' as CSSProperties['overflowY'],
            }
        }

        // Default position (downward)
        return downwardPosition
    }, [
        dropdownItemCount,
        dropdownItemHeight,
    ])

    const handleMouseEnter = () => {
        const newPositionStyles = getPositionStyles()

        setPositionStyles(newPositionStyles)
        setIsVisible(true)
    }

    const handleMouseLeave = () => {
        setIsVisible(false)
    }

    useEffect(() => {
        const handleResize = () => {
            if (isVisible) {
                const newPositionStyles = getPositionStyles()

                setPositionStyles(newPositionStyles)
            }
        }

        window.addEventListener('resize', handleResize)
        return () => {
            return window.removeEventListener('resize', handleResize)
        }
    }, [
        getPositionStyles,
        isVisible,
        dropdownItemCount,
    ])
    return {
        isVisible,
        positionStyles,
        dropdownRef,
        containerRef,
        handleMouseEnter,
        handleMouseLeave,
    }
}

export default useSmartDropdownPositioning
